import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { modalIds } from "constants/modals-constants";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";

import { useCurrentPathMatch } from "hooks/useCurrentPathMatch";
import { useModal } from "hooks/useModal";
import { useProduct } from "hooks/useProduct";
import { useProfile } from "hooks/useProfile";
import { useLanguage } from "hooks/useSelectedLanguage";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import useRequest from "hooks/user/useRequest";

import { StudentNavigationMenu } from "../components/NavigationMenu/StudentNavigationMenu";

import { MainPageLayout } from "components/MainPageLayout";
import { StudentHeader } from "../components/Header/StudentHeader";

// Pages where the side menu should not appear
// @see https://github.com/infinitaslearning/app-he-hoges-web/pull/1543
const routesWithoutSideMenu: string[] = [];

export const StudentDashboard: React.FC = observer(() => {
  const navigate = useNavigate();
  const { toggleModal } = useModal();
  const { currentLanguage } = useLanguage();
  const selectedProductId = useSelectedProductId();
  const { fetchUserDetails, userDetails, updateUserDetails } = useProfile();

  const { logout } = useRequest();
  const { currentRouteMatchesAny, currentRouteMatches } = useCurrentPathMatch();

  const { fetchProducts, fetchProductDetails, productsList } = useProduct();

  useEffect(() => {
    fetchUserDetails();
    fetchProducts();
  }, [fetchProducts, fetchUserDetails]);

  // Auto-select first product when list is loaded
  useEffect(() => {
    if (!selectedProductId && productsList && productsList.length > 0) {
      navigate(
        buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS, {
          productId: productsList[0].id,
        }),
      );
    }
  }, [JSON.stringify(productsList)]);

  // Reload product details when user switches their language (because the module descriptions will change)
  // If we're in expired license path, we won't attempt to fetch data to avoid entering an infinite loop
  useEffect(() => {
    if (selectedProductId && !currentRouteMatches(PRODUCT_PAGES_ROUTES.EXPIRED_LICENSE)) {
      fetchProductDetails(+selectedProductId);
    }
  }, [fetchProductDetails, selectedProductId, currentLanguage]);

  const onToggleSideMenu = () => {
    toggleModal(modalIds.modalSideMenu);
  };

  const shouldShowSideNav = !currentRouteMatchesAny(routesWithoutSideMenu);

  return (
    <MainPageLayout
      header={
        <StudentHeader
          toggleSideMenu={onToggleSideMenu}
          updateUserDetails={updateUserDetails}
          userDetails={userDetails}
          onLogout={logout}
        />
      }
      navigationMenu={
        <StudentNavigationMenu
          dataCy="product-navigation-menu"
          toggleSideMenu={onToggleSideMenu}
          updateUserDetails={updateUserDetails}
          userDetails={userDetails}
          onLogout={logout}
        />
      }
      shouldShowHeader={false}
      shouldShowSideNav={shouldShowSideNav}
      className="studentDashboardNoordhoffRedesign"
    >
      <Outlet />
    </MainPageLayout>
  );
});
